import { useEffect } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from './Icon';
import { isEmbedded } from '../../utils/iframe';

// ESC / backdrop ile yanlışlıkla kapanmayı önlemek için no-op listener
const _noop = () => {};

/**
 * Modal — iframe içinde çalışmak üzere body scroll lock + ESC kapanır.
 * size: sm | md | lg | xl | full
 *
 * Embedded modda (spine_app içine gömüldüğünde), modal açılır/kapanır
 * bilgisini parent'a postMessage ile gönderiyoruz. Böylece spine kendi
 * layout'unda full-viewport backdrop overlay render edebiliyor — blur
 * yalnızca iframe alanını değil, spine'ın sidebar/header/padding
 * gap'lerini de kapsıyor. Overlay sadece görsel; tıklama/ESC yine
 * iframe içindeki modal'a geliyor.
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = false,
}) {
  useEffect(() => {
    if (!open) return;
    // ESC ile kapanma intentionally devre dışı — sadece footer'daki İptal/Kapat
    // butonu veya başlıktaki X butonu modal'ı kapatır. Yanlışlıkla kapanma
    // (form dolu iken ESC, backdrop tıklama) veri kaybına yol açıyor.
    window.addEventListener('keydown', _noop);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Embedded modda parent'a modal açıldığını bildir.
    let notified = false;
    if (isEmbedded() && typeof window !== 'undefined' && window.parent) {
      try {
        window.parent.postMessage(
          { source: 'rule-engine', type: 'modal:open' },
          '*',
        );
        notified = true;
      } catch {
        /* sessizce yut — parent farklı origin'de iframe bridge ile çalışıyor */
      }
    }

    return () => {
      window.removeEventListener('keydown', _noop);
      document.body.style.overflow = prev;
      if (notified) {
        try {
          window.parent.postMessage(
            { source: 'rule-engine', type: 'modal:close' },
            '*',
          );
        } catch {
          /* no-op */
        }
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-[95vw]',
  };

  // İframe içinde kendi backdrop'ımızı her zaman render ediyoruz (iframe
  // dikdörtgenini kaplar). Embedded modda spine bizi destekler: iframe'in
  // etrafındaki spine padding/gutter alanlarına 4 strip overlay koyar,
  // böylece iframe alanı + çevresi birleşik tek blur yüzeyi gibi görünür.
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-neutral-900/50 backdrop-blur-sm"
      onClick={undefined}
    >
      <div
        className={cn(
          // Shared Zuzzuu 2.0 modal: 20px radius + yumuşak gölge
          'w-full rounded-2xl bg-white shadow-modal animate-fadein',
          sizes[size],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-5">
            <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
            {/* Shared PreviewModal ile aynı yuvarlak close butonu. */}
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 transition-colors hover:bg-neutral-50 hover:text-neutral-900"
              aria-label="Kapat"
            >
              <Icon name="close" className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
